import { useState } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import { type FieldValues, type Path, useController } from 'react-hook-form'

import { useUnique } from '../../Unique'
import type { CommonFieldProps } from '../CommonProps'
import { useFormOptions } from '../useFormOptions'

type Props<TPath extends string> = Pick<
	CommonFieldProps<TPath>,
	'name' | 'isDisabled' | 'isReadOnly' | 'showChanged' | 'isRequired'
>

export const useCheckbox = <TFieldValues extends FieldValues, TPath extends Path<TFieldValues>>({
	name,
	isDisabled = false,
	isReadOnly = false,
	isRequired = false,
	showChanged,
}: Props<TPath>) => {
	const id = useUnique(name)
	const {
		field: { value, onChange, onBlur, ref },
		fieldState: { error: fieldError, invalid: isInvalid },
	} = useController<FieldValues, Path<FieldValues>>({
		name,
		disabled: isDisabled,
		defaultValue: false,
	})
	const isChecked = Boolean(value)
	const [initialIsChecked] = useState(isChecked)
	const { isFocusVisible, focusProps } = useFocusRing({ within: true })
	const { hoverProps, isHovered } = useHover({})
	const { showChangedFields } = useFormOptions()
	const hasError = Boolean(fieldError || isInvalid)

	return {
		id,
		isChecked,
		initialIsChecked,
		isFocusVisible,
		isHovered,
		isRequired,
		error: fieldError,
		hasError,
		focusProps,
		hoverProps,
		showChangedFields,
		showChanged,
		inputProps: {
			ref,
			id,
			name,
			checked: isChecked,
			disabled: isDisabled || isReadOnly,
			onChange: (event: { target: { checked: boolean } }) => onChange(event.target.checked),
			required: Boolean(isRequired),
			onBlur,
			type: 'checkbox' as const,
		},
	}
}
